import React, { useState, useEffect } from "react";
import {
  ResponsiveContainer,
  ComposedChart,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  Label,
  BarChart,
  Bar,
  Area,
} from "recharts";

// ───────────────────── Custom Native Markdown Renderer ─────────────────────
const NativeAnalysis = ({ content }) => {
  if (!content) return null;
  if (typeof content !== "string") return null;

  const parseInline = (text) => {
    const parts = text.split(/(\*\*.*?\*\*|_.*?_)/g);
    return parts.map((part, index) => {
      if (part.startsWith("**") && part.endsWith("**")) {
        return (
          <strong key={index} className="font-bold text-[#323334]">
            {part.slice(2, -2)}
          </strong>
        );
      } else if (part.startsWith("_") && part.endsWith("_")) {
        return (
          <em key={index} className="italic text-[#323334]">
            {part.slice(1, -1)}
          </em>
        );
      }
      return part;
    });
  };

  const lines = content.split("\n");

  return (
    <div className="font-sans text-[#7E8081] space-y-2 text-[15px] leading-[19.5px]">
      {lines.map((line, index) => {
        const trimmed = line.trim();
        if (!trimmed) return <div key={index} className="h-1"></div>;

        if (trimmed.startsWith("### "))
          return (
            <h3
              key={index}
              className="text-[16px] font-bold text-[#323334] mt-2 mb-1"
            >
              {parseInline(trimmed.slice(4))}
            </h3>
          );
        if (trimmed.startsWith("- ") || trimmed.startsWith("* ")) {
          return (
            <div key={index} className="flex items-start ml-2 mb-1">
              <span className="mr-2 text-[#59AD31] text-lg leading-none">•</span>
              <span>{parseInline(trimmed.slice(2))}</span>
            </div>
          );
        }

        return (
          <p key={index} className="mb-2">
            {parseInline(trimmed)}
          </p>
        );
      })}
    </div>
  );
};

const LoadingAnalysisHelper = () => (
  <div className="flex items-center gap-2 text-[#7E8081] text-sm md:text-base animate-pulse">
    <svg className="animate-spin h-5 w-5 text-[#59AD31]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
    </svg>
    <span className="font-bold">Generating analysis...</span>
  </div>
);

function extractAnalysisText(resData, fallbackKeys = []) {
  if (!resData || typeof resData !== "object") {
    return "Could not load analysis at this time.";
  }

  if (typeof resData.analysis === "string" && resData.analysis.trim()) {
    return resData.analysis;
  }

  for (const key of fallbackKeys) {
    if (typeof resData[key] === "string" && resData[key].trim()) {
      return resData[key];
    }
  }

  return "Could not load analysis at this time.";
}

function getTooltipIcon(entry, chartType = "bar") {
  const key = entry.dataKey;

  if (key === "targetShare") {
    return (
      <svg width="16" height="3" viewBox="0 0 16 3" className="inline">
        <line x1="0" y1="1.5" x2="16" y2="1.5" stroke="#FF0000" strokeWidth="2" strokeDasharray="3,2" />
      </svg>
    );
  }

  if (chartType === "line") {
    return (
      <svg width="16" height="2" viewBox="0 0 16 2" className="inline">
        <line x1="0" y1="1" x2="16" y2="1" stroke={entry.color} strokeWidth="2" />
      </svg>
    );
  }

  if (chartType === "area") {
    return (
      <span
        className="h-[3px] w-5 rounded-[1px]"
        style={{ backgroundColor: entry.color }}
      />
    );
  }

  // Standard Lines / Bars
  return (
    <span
      className="h-[3px] w-4 rounded-[2px]"
      style={{ backgroundColor: entry.color }}
    />
  );
}

// ───────── tooltip custom (ordenado por valor) ─────────
function CustomTooltip({ active, payload, label, unitType = "percent", chartType = "bar" }) {
  if (!active || !payload || payload.length === 0) return null;

  const sortedPayload = [...payload].sort((a, b) => {
    const av = typeof a.value === "number" ? a.value : 0;
    const bv = typeof b.value === "number" ? b.value : 0;
    return bv - av;
  });

  return (
    <div className="rounded-lg border border-[#EDF0F2] bg-white px-3 py-2 text-[11px] shadow-md">
      <div className="mb-1 text-[11px] font-medium text-[#323334]">{label}</div>
      {sortedPayload.map((item) => (
        <div
          key={item.dataKey}
          className="mb-0.5 flex items-center justify-between gap-2"
        >
          <div className="flex items-center gap-1.5">
            {getTooltipIcon(item, chartType)}
            <span className="text-[#7E8081]">{item.name}</span>
          </div>
          <span className="font-medium text-[#323334]">
            {typeof item.value === "number"
              ? unitType === "percent"
                ? item.value.toLocaleString("en-US", {
                  style: "percent",
                  maximumFractionDigits: 1,
                })
                : item.value.toLocaleString("en-US", {
                  maximumFractionDigits: 0,
                })
              : item.value}
            {unitType === "percent" && (
              <span className="text-[#98999A]"> %</span>
            )}
          </span>
        </div>
      ))}
    </div>
  );
}

// ───────── leyenda custom centrada y clicable ─────────
function BiodiversityLegend({ payload, hiddenKeys, onToggle, chartType = "line" }) {
  if (!payload) return null;

  return (
    <div className="mb-2 flex flex-wrap items-center justify-center gap-4 text-[12px] text-[#98999A]">
      {payload.map((entry) => {
        const isHidden = hiddenKeys.includes(entry.dataKey);

        return (
          <button
            key={entry.dataKey}
            type="button"
            onClick={() => onToggle(entry)}
            className={`flex items-center gap-1.5 ${isHidden ? "opacity-40" : ""
              }`}
          >
            {/* Icono según el tipo de gráfica */}
            {entry.dataKey === "targetShare" ? (
              <svg width="20" height="3" viewBox="0 0 20 3" className="inline">
                <line x1="0" y1="1.5" x2="20" y2="1.5" stroke="#FF0000" strokeWidth="2" strokeDasharray="4,2" />
              </svg>
            ) : chartType === "line" ? (
              <svg width="20" height="2" viewBox="0 0 20 2" className="inline">
                <line x1="0" y1="1" x2="20" y2="1" stroke={entry.color || "#9999CC"} strokeWidth="2" />
              </svg>
            ) : (
              <span
                className="h-[4px] w-5 rounded-[2px]"
                style={{ backgroundColor: entry.color }}
              />
            )}

            <span className="text-[12px]">{entry.value}</span>
          </button>
        );
      })}
    </div>
  );
}

// ───────────────────── Main Component ─────────────────────
export default function BiodiversityTab({ data, pathway, pathwayId, country, onLoadingChange }) {
  const [hiddenKeys, setHiddenKeys] = useState([]);
  const [biodiversityChartMode, setBiodiversityChartMode] = useState("line");
  const [protectedChartMode, setProtectedChartMode] = useState("bar");

  // 🔄 Limpiar estados cuando cambian los datos
  useEffect(() => {
    setHiddenKeys([]);
  }, [data]);

  // Extract the specific datasets
  const biodiversityShareData = data
    ? data["Share of total land that supports biodiversity conservation"]
    : [];

  const protectedEvolutionData = data
    ? data["Evolution of protected areas"]
    : [];

  // States for LLM Analysis (Chart 1)
  const [biodivAnalysis, setBiodivAnalysis] = useState("");
  const [loadingAnalysis, setLoadingAnalysis] = useState(false);

  // States for LLM Analysis (Chart 2)
  const [protectedAreaAnalysis, setProtectedAreaAnalysis] = useState("");
  const [loadingAnalysisProtectedArea, setLoadingAnalysisProtectedArea] =
    useState(false);

  useEffect(() => {
    onLoadingChange?.(loadingAnalysis || loadingAnalysisProtectedArea);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingAnalysis, loadingAnalysisProtectedArea]);

  // ───────────────────── Data Fetching (Analysis 1) ─────────────────────
  const fetchAnalysis = async () => {
    if (!biodiversityShareData || biodiversityShareData.length === 0 || !pathwayId || !country) {
      setBiodivAnalysis("");
      setLoadingAnalysis(false);
      return;
    }

    setLoadingAnalysis(true);
    try {
      const payload = {
        analysisKey: "biodiversity_analysis",
        scope: "country",
        country: country,
        biodiversityData: {
          data: JSON.stringify(biodiversityShareData),
          title: `Share of total land that supports biodiversity conservation - ${country}`,
        },
        context: "biodiversity",
        pathway,
        payload: {
          meta: {
            pathway_id: pathwayId,
            country: country,
            chart: "Land where natural processes predominate",
          },
        },
      };

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/chat/fablegpt`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`Analysis API error: ${response.status}`);
      }

      const resData = await response.json();
      setBiodivAnalysis(extractAnalysisText(resData, ["biodiversityAnalysis", "analysis"]));
    } catch (error) {
      console.error("Error fetching analysis:", error);
      setBiodivAnalysis("Cannot load analysis at this time.");
    } finally {
      setLoadingAnalysis(false);
    }
  };

  useEffect(() => {
    fetchAnalysis();
    // eslint-disable-next-line
  }, [biodiversityShareData, pathway, pathwayId, country]);

  // ───────────────────── Data Fetching (Analysis 2) ─────────────────────
  const fetchProtectedAnalysis = async () => {
    if (!protectedEvolutionData || protectedEvolutionData.length === 0 || !pathwayId || !country) {
      setProtectedAreaAnalysis("");
      setLoadingAnalysisProtectedArea(false);
      return;
    }

    setLoadingAnalysisProtectedArea(true);
    try {
      const payload = {
        analysisKey: "biodiversity_analysis_protected_area",
        scope: "country",
        country: country,
        protectedAreaData: {
          data: JSON.stringify(protectedEvolutionData),
          title: `Evolution of protected areas - ${country}`,
        },
        context: "protected_area",
        pathway,
        payload: {
          meta: {
            pathway_id: pathwayId,
            country: country,
            chart: "Evolution of protected areas",
          },
        },
      };

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/chat/fablegpt`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`Analysis API error: ${response.status}`);
      }

      const resData = await response.json();
      setProtectedAreaAnalysis(extractAnalysisText(resData, ["biodiversityAnalysisProtectedArea", "analysis"]));
    } catch (error) {
      console.error("Error fetching protected area analysis:", error);
      setProtectedAreaAnalysis("Cannot load analysis at this time.");
    } finally {
      setLoadingAnalysisProtectedArea(false);
    }
  };

  useEffect(() => {
    fetchProtectedAnalysis();
    // eslint-disable-next-line
  }, [protectedEvolutionData, pathway, pathwayId, country]);

  const handleLegendClick = (o) => {
    const { dataKey } = o;
    if (!dataKey) return;
    setHiddenKeys((prev) =>
      prev.includes(dataKey)
        ? prev.filter((k) => k !== dataKey)
        : [...prev, dataKey]
    );
  };

  return (
    <div className="flex w-full flex-col gap-10">
      {/* ──────────────── CHART 1: Percentage Share ──────────────── */}
      <section className="flex w-full flex-col gap-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <h2 className="text-[20px] font-medium text-[#323334]">
            Land where natural processes predominate
          </h2>
          <div className="flex flex-wrap items-center gap-2 mt-2 sm:mt-0">
            <div className="flex rounded-full border border-[#D9DCDC] bg-white p-1 shadow-sm">
              {[
                { value: "line", label: "Line" },
                { value: "bar", label: "Bar" },
                { value: "area", label: "Area" },
              ].map((mode) => {
                const active = biodiversityChartMode === mode.value;
                return (
                  <button
                    key={mode.value}
                    type="button"
                    onClick={() => setBiodiversityChartMode(mode.value)}
                    className={`rounded-full px-3 py-1 text-sm transition ${active ? "bg-[#59AD31] text-white" : "text-[#7E8081] hover:bg-[#F4F7F6]"}`}
                  >
                    {mode.label}
                  </button>
                );
              })}
            </div>
            <button
              type="button"
              className="group flex items-center p-0 w-10 h-10 rounded-full bg-gradient-to-tr from-[#1E5B4F] to-[#C49A1F] text-white shadow-md hover:shadow-lg hover:scale-105 transition-all duration-200 overflow-hidden hover:w-44 text-center focus:outline-none focus:ring-2 focus:ring-[#C49A1F]"
              style={{ transitionProperty: 'width,background,color,box-shadow,transform', minWidth: '2.5rem', maxWidth: '11rem' }}
              aria-label="Download image"
              onClick={async () => {
                const chartSection = document.getElementById('biodiv-share-chart-section');
                if (!chartSection) return;
                const description = 'Land where natural processes predominate';
                const html2canvas = (await import('html2canvas')).default;
                html2canvas(chartSection, { scale: 2, useCORS: true }).then((canvas) => {
                  canvas.toBlob((blob) => {
                    if (!blob) return;
                    const url = URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = `${description.replace(/\s+/g, '_')}.jpg`;
                    link.click();
                    URL.revokeObjectURL(url);
                  }, 'image/jpeg', 0.95);
                });
              }}
            >
              <span className="relative w-10 h-10 transition-all duration-200">
                {/* Icono de imagen */}
                <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 flex-shrink-0 transition-all duration-200 group-hover:opacity-0 absolute inset-0 m-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <rect x="3" y="5" width="18" height="14" rx="2" fill="none" stroke="currentColor" strokeWidth="2" />
                  <circle cx="8.5" cy="10.5" r="1.5" fill="currentColor" />
                  <path d="M21 19l-5.5-7-4.5 6-3-4-4 5" stroke="currentColor" strokeWidth="2" fill="none" />
                </svg>
              </span>
              <span className="max-w-0 opacity-0 group-hover:max-w-[160px] group-hover:opacity-100 transition-all duration-200 whitespace-nowrap text-sm">Download image</span>
            </button>
            <button
              type="button"
              className="group flex items-center p-0 w-10 h-10 rounded-full bg-gradient-to-tr from-[#59AD31] to-[#C49A1F] text-white shadow-md hover:shadow-lg hover:scale-105 transition-all duration-200 overflow-hidden hover:w-44 text-center focus:outline-none focus:ring-2 focus:ring-[#59AD31]"
              style={{ transitionProperty: 'width,background,color,box-shadow,transform', minWidth: '2.5rem', maxWidth: '11rem' }}
              aria-label="Download data"
              onClick={async () => {
                const description = 'Land where natural processes predominate';
                const Papa = (await import('papaparse')).default;
                if (!biodiversityShareData || !Array.isArray(biodiversityShareData) || biodiversityShareData.length === 0) return;
                const headers = Object.keys(biodiversityShareData[0]);
                const csvRows = [
                  [`Description: ${description}`],
                  [],
                  headers,
                  ...biodiversityShareData.map(row => headers.map(h => row[h]))
                ];
                const csv = Papa.unparse(csvRows, { quotes: true, skipEmptyLines: true });
                const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
                const url = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = `${description.replace(/\s+/g, '_')}.csv`;
                link.click();
                URL.revokeObjectURL(url);
              }}
            >
              <span className="relative w-10 h-10 transition-all duration-200">
                {/* Icono de archivo clásico */}
                <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 flex-shrink-0 transition-all duration-200 group-hover:opacity-0 absolute inset-0 m-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path d="M6 2a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8.828A2 2 0 0 0 19.414 7.414l-4.828-4.828A2 2 0 0 0 13.172 2H6z" stroke="currentColor" strokeWidth="2"/>
                  <path d="M14 2v6a2 2 0 0 0 2 2h6" stroke="currentColor" strokeWidth="2"/>
                </svg>
              </span>
              <span className="max-w-0 opacity-0 group-hover:max-w-[160px] group-hover:opacity-100 transition-all duration-200 whitespace-nowrap text-sm">Download data</span>
            </button>
          </div>
        </div>

        <div id="biodiv-share-chart-section" className="h-[600px] rounded-xl border border-[#EDF0F2] px-6 py-4">
          <ResponsiveContainer width="100%" height="100%">
            {biodiversityChartMode === "bar" ? (
              <ComposedChart
                data={biodiversityShareData}
                margin={{ top: 40, right: 30, left: 60, bottom: 30 }}
              >
                <CartesianGrid strokeDasharray="3 3" />

                <XAxis
                  dataKey="year"
                  interval={0}
                  angle={-45}
                  textAnchor="end"
                  height={70}
                  tick={{ fill: "#98999A", fontSize: 12 }}
                  tickMargin={10}
                >
                  <Label
                    value="Years"
                    position="insideBottom"
                    offset={-20}
                    style={{ fill: "#98999A", fontSize: 15 }}
                  />
                </XAxis>

                <YAxis
                  width={60}
                  tick={{ fill: "#98999A", fontSize: 12 }}
                  tickFormatter={(v) =>
                    Number(v).toLocaleString("en-US", {
                      style: "percent",
                      maximumFractionDigits: 0,
                    })
                  }
                >
                  <Label
                    value="Percentage of area"
                    angle={-90}
                    position="left"
                    dx={-20}
                    style={{ fill: "#98999A", fontSize: 15 }}
                  />
                </YAxis>

                <Tooltip
                  content={(props) => <CustomTooltip {...props} unitType="percent" chartType="bar" />}
                  cursor={{ fill: "rgba(0,0,0,0.03)" }}
                />

                <Legend
                  verticalAlign="top"
                  align="center"
                  wrapperStyle={{ top: 0 }}
                  content={(props) => (
                    <BiodiversityLegend
                      {...props}
                      hiddenKeys={hiddenKeys}
                      onToggle={handleLegendClick}
                      chartType="bar"
                    />
                  )}
                />

                <Bar
                  dataKey="computedShare"
                  name="Calculated"
                  fill="#FFD786"
                  hide={hiddenKeys.includes("computedShare")}
                />

                <Line
                  type="monotone"
                  dataKey="targetShare"
                  name="Target"
                  stroke="#FF0000"
                  strokeWidth={2}
                  strokeDasharray="5 5"
                  dot={false}
                  hide={hiddenKeys.includes("targetShare")}
                />
              </ComposedChart>
            ) : (
              <ComposedChart
                data={biodiversityShareData}
                margin={{ top: 40, right: 30, left: 60, bottom: 30 }}
              >
                <CartesianGrid strokeDasharray="3 3" />

                <XAxis
                  dataKey="year"
                  interval={0}
                  angle={-45}
                  textAnchor="end"
                  height={70}
                  tick={{ fill: "#98999A", fontSize: 12 }}
                  tickMargin={10}
                >
                  <Label
                    value="Years"
                    position="insideBottom"
                    offset={-20}
                    style={{ fill: "#98999A", fontSize: 15 }}
                  />
                </XAxis>

                <YAxis
                  width={60}
                  tick={{ fill: "#98999A", fontSize: 12 }}
                  tickFormatter={(v) =>
                    Number(v).toLocaleString("en-US", {
                      style: "percent",
                      maximumFractionDigits: 0,
                    })
                  }
                >
                  <Label
                    value="Percentage of area"
                    angle={-90}
                    position="left"
                    dx={-20}
                    style={{ fill: "#98999A", fontSize: 15 }}
                  />
                </YAxis>

                <Tooltip
                  content={(props) => <CustomTooltip {...props} unitType="percent" chartType={biodiversityChartMode} />}
                  cursor={{ stroke: "rgba(0,0,0,0.15)", strokeWidth: 1 }}
                />

                <Legend
                  verticalAlign="top"
                  align="center"
                  wrapperStyle={{ top: 0 }}
                  content={(props) => (
                    <BiodiversityLegend
                      {...props}
                      hiddenKeys={hiddenKeys}
                      onToggle={handleLegendClick}
                      chartType={biodiversityChartMode}
                    />
                  )}
                />

                {biodiversityChartMode === "line" ? (
                  <>
                    <Line
                      type="monotone"
                      dataKey="computedShare"
                      name="Calculated"
                      stroke="#FFD786"
                      strokeWidth={2}
                      dot={false}
                      hide={hiddenKeys.includes("computedShare")}
                    />

                    <Line
                      type="monotone"
                      dataKey="targetShare"
                      name="Target"
                      stroke="#FF0000"
                      strokeWidth={2}
                      strokeDasharray="5 5"
                      dot={false}
                      hide={hiddenKeys.includes("targetShare")}
                    />
                  </>
                ) : (
                  <>
                    <Area
                      type="monotone"
                      dataKey="computedShare"
                      name="Calculated"
                      stroke="#FFD786"
                      fill="#FFD786"
                      fillOpacity={0.35}
                      dot={false}
                      hide={hiddenKeys.includes("computedShare")}
                    />

                    <Area
                      type="monotone"
                      dataKey="targetShare"
                      name="Target"
                      stroke="#FF0000"
                      fill="#FF0000"
                      fillOpacity={0.15}
                      dot={false}
                      hide={hiddenKeys.includes("targetShare")}
                    />
                  </>
                )}
              </ComposedChart>
            )}
          </ResponsiveContainer>
        </div>

        {/* Dynamic Analysis Text Chart 1 */}
        <div className="w-full min-h-[100px]">
          {loadingAnalysis ? (
            <LoadingAnalysisHelper />
          ) : (
            <div className="group relative">
              <button
                onClick={() => fetchAnalysis()}
                title="Regenerate analysis"
                className="absolute top-0 right-0 opacity-0 group-hover:opacity-100 p-2 text-[#98999A] hover:text-[#59AD31] transition-all bg-white rounded-full shadow-sm border border-gray-100"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </button>
              <div id="desc-chart_land_natural_processes"><NativeAnalysis content={biodivAnalysis} /></div>
            </div>
          )}
        </div>
      </section>

      {/* ──────────────── CHART 2: Evolution of Protected Areas (Stacked Bar) ──────────────── */}
      <section className="flex w-full flex-col gap-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <h2 className="text-[20px] font-medium text-[#323334]">
            Evolution of Protected Areas
          </h2>
          <div className="flex flex-wrap items-center gap-2 mt-2 sm:mt-0">
            <div className="flex rounded-full border border-[#D9DCDC] bg-white p-1 shadow-sm">
              {[
                { value: "bar", label: "Bar" },
                { value: "area", label: "Area" },
              ].map((mode) => {
                const active = protectedChartMode === mode.value;
                return (
                  <button
                    key={mode.value}
                    type="button"
                    onClick={() => setProtectedChartMode(mode.value)}
                    className={`rounded-full px-3 py-1 text-sm transition ${active ? "bg-[#59AD31] text-white" : "text-[#7E8081] hover:bg-[#F4F7F6]"}`}
                  >
                    {mode.label}
                  </button>
                );
              })}
            </div>
            <button
              type="button"
              className="group flex items-center p-0 w-10 h-10 rounded-full bg-gradient-to-tr from-[#1E5B4F] to-[#C49A1F] text-white shadow-md hover:shadow-lg hover:scale-105 transition-all duration-200 overflow-hidden hover:w-44 text-center focus:outline-none focus:ring-2 focus:ring-[#C49A1F]"
              style={{ transitionProperty: 'width,background,color,box-shadow,transform', minWidth: '2.5rem', maxWidth: '11rem' }}
              aria-label="Download image"
              onClick={async () => {
                // Download image as JPG using html2canvas (chart only)
                const chartSection = document.getElementById('biodiv-protected-chart-section');
                if (!chartSection) return;
                const description = 'Evolution of Protected Areas';
                const html2canvas = (await import('html2canvas')).default;
                html2canvas(chartSection, { scale: 2, useCORS: true }).then((canvas) => {
                  canvas.toBlob((blob) => {
                    if (!blob) return;
                    const url = URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = `${description.replace(/\s+/g, '_')}.jpg`;
                    link.click();
                    URL.revokeObjectURL(url);
                  }, 'image/jpeg', 0.95);
                });
              }}
            >
              <span className="relative w-10 h-10 transition-all duration-200">
                {/* Icono de imagen */}
                <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 flex-shrink-0 transition-all duration-200 group-hover:opacity-0 absolute inset-0 m-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <rect x="3" y="5" width="18" height="14" rx="2" fill="none" stroke="currentColor" strokeWidth="2" />
                  <circle cx="8.5" cy="10.5" r="1.5" fill="currentColor" />
                  <path d="M21 19l-5.5-7-4.5 6-3-4-4 5" stroke="currentColor" strokeWidth="2" fill="none" />
                </svg>
              </span>
              <span className="max-w-0 opacity-0 group-hover:max-w-[160px] group-hover:opacity-100 transition-all duration-200 whitespace-nowrap text-sm">Download image</span>
            </button>
            <button
              type="button"
              className="group flex items-center p-0 w-10 h-10 rounded-full bg-gradient-to-tr from-[#59AD31] to-[#C49A1F] text-white shadow-md hover:shadow-lg hover:scale-105 transition-all duration-200 overflow-hidden hover:w-44 text-center focus:outline-none focus:ring-2 focus:ring-[#59AD31]"
              style={{ transitionProperty: 'width,background,color,box-shadow,transform', minWidth: '2.5rem', maxWidth: '11rem' }}
              aria-label="Download data"
              onClick={async () => {
                // Download data as CSV using papaparse, better formatted
                const description = 'Evolution of Protected Areas';
                const Papa = (await import('papaparse')).default;
                if (!protectedEvolutionData || !Array.isArray(protectedEvolutionData) || protectedEvolutionData.length === 0) return;
                // Readable headers
                const headers = Object.keys(protectedEvolutionData[0]);
                const csvRows = [
                  [`Description: ${description}`],
                  [],
                  headers,
                  ...protectedEvolutionData.map(row => headers.map(h => row[h]))
                ];
                const csv = Papa.unparse(csvRows, { quotes: true, skipEmptyLines: true });
                const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
                const url = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = `${description.replace(/\s+/g, '_')}.csv`;
                link.click();
                URL.revokeObjectURL(url);
              }}
            >
              <span className="relative w-10 h-10 transition-all duration-200">
                {/* Icono de archivo clásico */}
                <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 flex-shrink-0 transition-all duration-200 group-hover:opacity-0 absolute inset-0 m-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path d="M6 2a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8.828A2 2 0 0 0 19.414 7.414l-4.828-4.828A2 2 0 0 0 13.172 2H6z" stroke="currentColor" strokeWidth="2"/>
                  <path d="M14 2v6a2 2 0 0 0 2 2h6" stroke="currentColor" strokeWidth="2"/>
                </svg>
              </span>
              <span className="max-w-0 opacity-0 group-hover:max-w-[160px] group-hover:opacity-100 transition-all duration-200 whitespace-nowrap text-sm">Download data</span>
            </button>
          </div>
        </div>

        <div id="biodiv-protected-chart-section" className="h-[600px] rounded-xl border border-[#EDF0F2] px-6 py-4">
          <ResponsiveContainer width="100%" height="100%">
            {protectedChartMode === "bar" ? (
              <BarChart
                data={protectedEvolutionData}
                margin={{ top: 40, right: 30, left: 60, bottom: 30 }}
              >
                <CartesianGrid strokeDasharray="3 3" vertical={false} />

                <XAxis
                  dataKey="year"
                  interval={0}
                  angle={-45}
                  textAnchor="end"
                  height={70}
                  tick={{ fill: "#98999A", fontSize: 12 }}
                  tickMargin={10}
                >
                  <Label
                    value="Years"
                    position="insideBottom"
                    offset={-20}
                    style={{ fill: "#98999A", fontSize: 15 }}
                  />
                </XAxis>

                <YAxis
                  width={60}
                  tick={{ fill: "#98999A", fontSize: 12 }}
                  tickFormatter={(v) =>
                    Number(v).toLocaleString("en-US", {
                      notation: "compact",
                      compactDisplay: "short",
                    })
                  }
                >
                  <Label
                    value="1000 ha"
                    angle={-90}
                    position="left"
                    dx={-20}
                    style={{ fill: "#98999A", fontSize: 15 }}
                  />
                </YAxis>

                <Tooltip
                  content={(props) => <CustomTooltip {...props} unitType="number" chartType="bar" />}
                  cursor={{ fill: "rgba(0,0,0,0.05)" }}
                />

                <Legend
                  verticalAlign="top"
                  align="center"
                  wrapperStyle={{ top: 0 }}
                  content={(props) => (
                    <BiodiversityLegend
                      {...props}
                      hiddenKeys={hiddenKeys}
                      onToggle={handleLegendClick}
                      chartType="bar"
                    />
                  )}
                />

                <Bar
                  dataKey="forests"
                  name="Forest"
                  stackId="protected"
                  fill="#4CAF50"
                  hide={hiddenKeys.includes("forests")}
                />

                <Bar
                  dataKey="otherLands"
                  name="Otherlands (shrublands, wetlands, grasslands)"
                  stackId="protected"
                  fill="#FFC107"
                  hide={hiddenKeys.includes("otherLands")}
                />

                <Bar
                  dataKey="miscellaneous"
                  name="Miscellaneous (agriculture, urban, others)"
                  stackId="protected"
                  fill="#607D8B"
                  hide={hiddenKeys.includes("miscellaneous")}
                />
              </BarChart>
            ) : (
              <ComposedChart
                data={protectedEvolutionData}
                margin={{ top: 40, right: 30, left: 60, bottom: 30 }}
              >
                <CartesianGrid strokeDasharray="3 3" vertical={false} />

                <XAxis
                  dataKey="year"
                  interval={0}
                  angle={-45}
                  textAnchor="end"
                  height={70}
                  tick={{ fill: "#98999A", fontSize: 12 }}
                  tickMargin={10}
                >
                  <Label
                    value="Years"
                    position="insideBottom"
                    offset={-20}
                    style={{ fill: "#98999A", fontSize: 15 }}
                  />
                </XAxis>

                <YAxis
                  width={60}
                  tick={{ fill: "#98999A", fontSize: 12 }}
                  tickFormatter={(v) =>
                    Number(v).toLocaleString("en-US", {
                      notation: "compact",
                      compactDisplay: "short",
                    })
                  }
                >
                  <Label
                    value="1000 ha"
                    angle={-90}
                    position="left"
                    dx={-20}
                    style={{ fill: "#98999A", fontSize: 15 }}
                  />
                </YAxis>

                <Tooltip
                  content={(props) => <CustomTooltip {...props} unitType="number" chartType="area" />}
                  cursor={{ fill: "rgba(0,0,0,0.05)" }}
                />

                <Legend
                  verticalAlign="top"
                  align="center"
                  wrapperStyle={{ top: 0 }}
                  content={(props) => (
                    <BiodiversityLegend
                      {...props}
                      hiddenKeys={hiddenKeys}
                      onToggle={handleLegendClick}
                      chartType="area"
                    />
                  )}
                />

                <Area
                  type="monotone"
                  dataKey="forests"
                  name="Forest"
                  stackId="protected-area"
                  stroke="#4CAF50"
                  fill="#4CAF50"
                  fillOpacity={0.35}
                  hide={hiddenKeys.includes("forests")}
                />

                <Area
                  type="monotone"
                  dataKey="otherLands"
                  name="Otherlands"
                  stackId="protected-area"
                  stroke="#FFC107"
                  fill="#FFC107"
                  fillOpacity={0.35}
                  hide={hiddenKeys.includes("otherLands")}
                />

                <Area
                  type="monotone"
                  dataKey="miscellaneous"
                  name="Miscellaneous"
                  stackId="protected-area"
                  stroke="#607D8B"
                  fill="#607D8B"
                  fillOpacity={0.35}
                  hide={hiddenKeys.includes("miscellaneous")}
                />
              </ComposedChart>
            )}
          </ResponsiveContainer>
        </div>

        {/* Dynamic Analysis Text Chart 2 */}
        <div className="w-full min-h-[100px]">
          {loadingAnalysisProtectedArea ? (
            <LoadingAnalysisHelper />
          ) : (
            <div className="group relative">
              <button
                onClick={() => fetchProtectedAnalysis()}
                title="Regenerate analysis"
                className="absolute top-0 right-0 opacity-0 group-hover:opacity-100 p-2 text-[#98999A] hover:text-[#59AD31] transition-all bg-white rounded-full shadow-sm border border-gray-100"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </button>
              <div id="desc-chart_evolution_protected_areas"><NativeAnalysis content={protectedAreaAnalysis} /></div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}